import { useNavigate } from "react-router-dom";
import { useDashboardAssessments } from "@/hooks/useDashboardAssessments";
import { Button } from "@/components/ui/button";
import {
  ClipboardList, Calendar, Building2, DollarSign, TrendingUp,
  Users, PiggyBank, GitBranch, Shield, FileText, BookOpen,
  type LucideIcon,
} from "lucide-react";

const MODULE_TITLES: Record<string, string> = {
  "owner-readiness": "mySuccession Snapshot",
  "owner-readiness-exit-timeline": "Readiness & Exit Timeline",
  "business-profile": "Business Profile",
  "business-valuation": "Business Valuation",
  "business-optimization": "Business Optimization Review",
  "advisory-team": "Advisory Team",
  "financial-readiness": "Financial Readiness",
  "successor-strategy": "mySuccession Strategy",
  "governance-family": "Governance & Family",
  "exit-structure": "Exit Structure & Tax Optimization",
  "mysuccession-plan": "mySuccession Plan Assembly",
};

const MODULE_ICONS: Record<string, LucideIcon> = {
  "owner-readiness": ClipboardList,
  "owner-readiness-exit-timeline": Calendar,
  "business-profile": Building2,
  "business-valuation": DollarSign,
  "business-optimization": TrendingUp,
  "advisory-team": Users,
  "financial-readiness": PiggyBank,
  "successor-strategy": GitBranch,
  "governance-family": Shield,
  "exit-structure": FileText,
  "mysuccession-plan": BookOpen,
};

const MODULE_BLURBS: Record<string, string> = {
  "owner-readiness": "Every successful transition starts with an honest look in the mirror - this is yours.",
  "owner-readiness-exit-timeline": "When do you want to walk away, and are you really ready? Let's find out.",
  "business-profile": "Your practice has a story - the more we know, the stronger your plan.",
  "business-valuation": "You've spent decades building it. Let's make sure you know what it's worth.",
  "business-optimization": "A stronger practice today means a better deal and more options tomorrow. Let's review together.",
  "advisory-team": "The right people in your corner can be the difference between a good exit and a great one.",
  "financial-readiness": "The practice funds your life today - what funds it after?",
  "successor-strategy": "Who carries this forward? This is the question that changes everything.",
  "governance-family": "Family and money are complicated - getting this right protects everyone.",
  "exit-structure": "How you exit is just as important as when - structure it to keep more of what you built.",
  "mysuccession-plan": "This is what everything has been building toward - your plan, in full.",
};

interface RecommendedNextStepContentProps {
  onModuleSelect?: (moduleId: string, title: string) => void;
}

export function RecommendedNextStepContent({ onModuleSelect }: RecommendedNextStepContentProps) {
  const navigate = useNavigate();
  const { firstIncompleteModule, firstIncompleteStatus, completedCount, totalAssessments } =
    useDashboardAssessments();

  const allComplete = completedCount >= totalAssessments;

  if (allComplete) {
    const Icon = MODULE_ICONS["mysuccession-plan"] || BookOpen;
    return (
      <div className="flex flex-col items-start gap-2 w-full h-full">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Icon className="h-6 w-6 text-foreground" />
        </div>
        <p className="text-sm font-bold text-foreground">mySuccession Plan Assembly</p>
        <p className="text-xs text-foreground/70 leading-relaxed">
          Your succession plan is complete — review your mySuccession Plan
        </p>
        <div className="flex-1" />
        <Button
          size="sm"
          onClick={() => navigate("/plan")}
          className="w-full rounded-lg text-xs text-primary-foreground"
          style={{ background: "hsl(var(--primary))" }}
        >
          View Plan
        </Button>
      </div>
    );
  }

  const moduleId = firstIncompleteModule;
  const title = MODULE_TITLES[moduleId] ?? moduleId;
  const Icon = MODULE_ICONS[moduleId] || ClipboardList;
  const blurb = MODULE_BLURBS[moduleId] || "";

  const handleClick = () => {
    if (onModuleSelect) {
      onModuleSelect(moduleId, title);
    } else {
      navigate("/assessment");
    }
  };

  return (
    <div className="flex flex-col items-start w-full h-full">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
        <Icon className="h-6 w-6 text-foreground" />
      </div>
      <p className="mt-3 text-sm font-bold text-foreground">{title}</p>
      <p className="mt-1.5 text-xs text-foreground/70 leading-relaxed">
        {blurb}
      </p>
      <div className="flex-1" />
      <Button
        size="sm"
        onClick={handleClick}
        className="mt-3 w-full rounded-lg text-xs text-primary-foreground"
        style={{ background: "hsl(var(--primary))" }}
      >
        Start Now
      </Button>
    </div>
  );
}
